import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, Download, CheckCircle, AlertCircle, History, Clock, FileText, Info, FileCode, RefreshCw, Files, FileDown, Layers, Link2, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useClient } from '../context/ClientContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Import extracted components
import { ImportHistory, NormalXMLInstructions, PartyDetailsUpload, UploadResult, ValidationWarnings } from '../components/import';
import { DuplicateFileModal, ImportPartySearchModal } from '../components/import/ImportModals';
import { ReferenceXMLImport } from '../components/import/ReferenceXMLImport';
import { PartyDetailsTab } from '../components/import/PartyDetailsTab';
import { UploadSection } from '../components/import/UploadSection';
import { devWarn } from "../utils/logger";
import { 
  formatTallyDate, 
  extractSection, 
  extractVoucherNo, 
  derivePanFromGstin,
  parseTDSLedgerXML,
  checkPanMismatches as checkPanMismatchesUtil
} from '../hooks/useXMLParser';
import {
  detectParentVoucherType,
  parseVoucherTypeMaster,
  parseVoucherTypeMasterXML,
  normalizeVoucherType,
} from '../utils/voucherParsers';
import {
  isCompanyPAN,
  getTDSRate,
  getExpectedRateFromPan,
  getExpenseAmountFromXML,
} from '../utils/tdsRates';

const API = process.env.REACT_APP_BACKEND_URL;

const TDSImport = () => {
  const { selectedClient, clientId } = useClient();
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [customLedgerName, setCustomLedgerName] = useState('');
  const [importType, setImportType] = useState('normal'); // 'normal', 'reference', 'tdl', 'party'
  const fileInputRef = useRef(null);
  const tdsRefFileRef = useRef(null);
  const mainFileRef = useRef(null);
  const vchMasterFileRef = useRef(null);
  const partyFileRef = useRef(null);
  
  // Reference upload state
  const [tdsRefData, setTdsRefData] = useState(null);
  const [mainFileData, setMainFileData] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  
  // Voucher Type Master state
  const [vchTypeMaster, setVchTypeMaster] = useState(null);
  
  // Skipped entries state (entries without voucher number)
  const [skippedEntries, setSkippedEntries] = useState([]);
  
  // Party details upload state
  const [partyData, setPartyData] = useState(null);
  const [partyUploadResult, setPartyUploadResult] = useState(null);
  
  // PAN mismatch and missing party state
  const [panMismatches, setPanMismatches] = useState([]);
  const [missingPartyEntries, setMissingPartyEntries] = useState([]);
  const [existingParties, setExistingParties] = useState([]);
  const [partySearchModal, setPartySearchModal] = useState({ open: false, txnIndex: null, searchQuery: '' });
  
  // Amount mismatch state (Amount=0 but TDS>0)
  const [amountMismatches, setAmountMismatches] = useState([]);
  
  // Pre-import validation state
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  
  // Duplicate handling modal
  const [duplicateModal, setDuplicateModal] = useState({ 
    open: false, 
    filename: '', 
    transactions: [], 
    existingCount: 0,
    pendingFiles: [],
    currentFileIndex: 0
  });

  const fetchImportHistory = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoadingHistory(true);
      const response = await axios.get(`${API}/api/import-history?client_id=${clientId}&limit=10`);
      setImportHistory(response.data || []);
    } catch (err) { devWarn("fetchImportHistory (non-critical):", err); } finally {
      setLoadingHistory(false);
    }
  }, [clientId]);

  const fetchExistingParties = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await axios.get(`${API}/api/ledgers?client_id=${clientId}`);
      setExistingParties(response.data || []);
    } catch (err) { devWarn("fetchExistingParties (non-critical):", err); }
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    // Defer to next microtask so the synchronous render commit completes first.
    queueMicrotask(() => {
      fetchImportHistory();
      fetchExistingParties();
    });
  }, [clientId, fetchImportHistory, fetchExistingParties]);

  // Check for PAN mismatches between XML and database
  const checkPanMismatches = (transactions) => {
    const mismatches = [];
    const missingParty = [];
    const amtMismatches = [];
    
    transactions.forEach((txn, idx) => {
      const xmlPan = (txn.pan || '').toUpperCase().trim();
      const partyName = (txn.party_name || '').trim();
      
      // Find matching party in database (case-insensitive)
      const dbParty = existingParties.find(p => 
        p.name?.toUpperCase() === partyName.toUpperCase()
      );
      
      if (dbParty) {
        const dbPan = (dbParty.pan || '').toUpperCase().trim();
        // Check for mismatch (both have PAN but different)
        if (xmlPan && dbPan && xmlPan !== dbPan) {
          mismatches.push({
            index: idx,
            party_name: partyName,
            voucher_no: txn.voucher_no,
            xml_pan: xmlPan,
            db_pan: dbPan,
            tds_amount: txn.tds_amount
          });
        }
      }
      
      // Track entries with missing PAN or party name
      if (!xmlPan || !partyName) {
        missingParty.push({
          index: idx,
          party_name: partyName || '(No Name)',
          voucher_no: txn.voucher_no,
          pan: xmlPan,
          tds_amount: txn.tds_amount,
          missing_pan: !xmlPan,
          missing_name: !partyName
        });
      }
      
      // Track amount mismatches (Amount=0 but TDS/TCS>0)
      if ((txn.amount === 0 || !txn.amount) && txn.tds_amount > 0) {
        amtMismatches.push({
          index: idx,
          party_name: partyName || '(No Name)',
          voucher_no: txn.voucher_no,
          amount: txn.amount || 0,
          tds_amount: txn.tds_amount,
          section: txn.section,
          is_tcs: txn.is_tcs
        });
      }
    });
    
    setPanMismatches(mismatches);
    setMissingPartyEntries(missingParty);
    setAmountMismatches(amtMismatches);
    
    return { mismatches, missingParty };
  };

  // Assign party to a transaction with missing info
  const assignPartyToTransaction = (txnIndex, party) => {
    if (!matchResult?.transactions) return;
    
    const updatedTxns = [...matchResult.transactions];
    updatedTxns[txnIndex] = {
      ...updatedTxns[txnIndex],
      party_name: party.name,
      pan: party.pan || updatedTxns[txnIndex].pan,
      gstin: party.gstin || updatedTxns[txnIndex].gstin,
      _partyAssigned: true
    };
    
    setMatchResult({
      ...matchResult,
      transactions: updatedTxns
    });
    
    // Re-check for remaining missing parties
    const stillMissing = missingPartyEntries.filter(m => m.index !== txnIndex);
    setMissingPartyEntries(stillMissing);
    
    setPartySearchModal({ open: false, txnIndex: null, searchQuery: '' });
    toast.success(`Assigned ${party.name} to transaction`);
  };

  // Parse TDL Export format (CSV with ledger names)
  const parseTDLExport = (text, filename) => {
    const transactions = [];
    const sectionFromFilename = extractSection(filename) || '';
    
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return transactions;
    
    // First line is header
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
    
    // Find column indices
    const dateIdx = headers.findIndex(h => h.includes('date'));
    const vchNoIdx = headers.findIndex(h => h.includes('voucherno') || h.includes('voucher'));
    const vchTypeIdx = headers.findIndex(h => h.includes('vouchertype') || h.includes('type'));
    const partyIdx = headers.findIndex(h => h.includes('party') || h.includes('name'));
    const panIdx = headers.findIndex(h => h.includes('pan'));
    const gstinIdx = headers.findIndex(h => h.includes('gstin'));
    const ledgerIdx = headers.findIndex(h => h.includes('ledger'));
    const amountIdx = headers.findIndex(h => h === 'amount' || h.includes('amount'));
    const tdsLedgerIdx = headers.findIndex(h => h.includes('tdsledger'));
    const tdsAmountIdx = headers.findIndex(h => h.includes('tdsamount'));
    const narrationIdx = headers.findIndex(h => h.includes('narration'));
    
    
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length < 5) continue;
      
      const date = values[dateIdx] || '';
      const partyName = values[partyIdx] || '';
      const pan = values[panIdx] || '';
      const amount = parseFloat((values[amountIdx] || '0').replace(/,/g, '')) || 0;
      const tdsAmount = parseFloat((values[tdsAmountIdx] || '0').replace(/,/g, '')) || 0;
      const tdsLedger = values[tdsLedgerIdx] || '';
      const gstin = values[gstinIdx] || '';
      
      // Skip rows without valid data
      if (!date || !partyName || tdsAmount <= 0) continue;
      
      // Derive PAN from GSTIN if PAN is not available
      let finalPan = pan.trim().toUpperCase();
      if (!finalPan && gstin && gstin.length >= 12) {
        finalPan = gstin.substring(2, 12).toUpperCase();
      }
      
      // Extract section from TDS ledger name
      const section = sectionFromFilename || extractSection(tdsLedger) || '';
      
      transactions.push({
        date: formatTallyDate(date),
        voucher_no: values[vchNoIdx] || '',
        voucher_type: values[vchTypeIdx] || '',
        party_name: partyName.trim(),
        pan: finalPan,
        gstin: gstin,
        narration: values[narrationIdx] || '',
        amount: Math.abs(amount),
        tds_amount: Math.abs(tdsAmount),
        section: section,
        dr_cr: 'Dr'
      });
    }
    
    return transactions;
  };

  // Parse Tally Columnar XML format
  const parseTallyColumnarXML = (xmlText, filename, tdsRefMap = null, masterMappings = null) => {
    const transactions = [];
    const skippedEntries = []; // Track entries skipped due to missing voucher info
    const sectionFromFilename = extractSection(filename) || null;
    
    let cleanXml = xmlText.replace(/^\uFEFF/, '').replace(/^\xEF\xBB\xBF/, '');
    
    // The XML structure has <DBCFIXED> containing date and party, followed by other fields
    // We need to find each transaction block by looking for the pattern
    // Split by <DBCFIXED> but include all content until the next <DBCFIXED>
    const blocks = [];
    const parts = cleanXml.split(/<DBCFIXED>/i);
    
    for (let i = 1; i < parts.length; i++) {
      // Each block is from <DBCFIXED> to the next <DBCFIXED>
      blocks.push(parts[i]);
    }
    
    blocks.forEach(block => {
      // Date and Party are inside DBCFIXED (before </DBCFIXED>)
      const dateMatch = block.match(/<DBCDATE>([^<]*)<\/DBCDATE>/i);
      const date = dateMatch ? dateMatch[1].trim() : null;
      
      const partyMatch = block.match(/<DBCPARTY>([^<]*)<\/DBCPARTY>/i);
      const partyName = partyMatch ? partyMatch[1].trim() : null;
      
      // Other fields are after </DBCFIXED>
      const vchTypeMatch = block.match(/<DBCVCHTYPE>([^<]*)<\/DBCVCHTYPE>/i);
      const voucherType = vchTypeMatch ? vchTypeMatch[1].trim() : null;
      
      const vchNoMatch = block.match(/<DBCVCHNO>([^<]*)<\/DBCVCHNO>/i);
      let voucherNo = vchNoMatch ? vchNoMatch[1].trim() : null;
      
      // Get DBCVCHREF for fallback
      const vchRefMatch = block.match(/<DBCVCHREF>([^<]*)<\/DBCVCHREF>/i);
      const vchRef = vchRefMatch ? vchRefMatch[1].trim() : null;
      
      // Fallback: If DBCVCHNO is empty, try to use DBCVCHREF as the voucher number
      if (!voucherNo && vchRef) {
        voucherNo = vchRef;
      }
      
      const gstinMatch = block.match(/<DBCGSTIN>([^<]*)<\/DBCGSTIN>/i);
      const gstin = gstinMatch ? gstinMatch[1].trim() : null;
      
      const panMatch = block.match(/<DBCPANNO>([^<]*)<\/DBCPANNO>/i);
      let pan = panMatch ? panMatch[1].trim() : null;
      
      // Derive PAN from GSTIN if PAN is not available
      // GSTIN format: 29AACCM2217L1ZE - PAN is 10 characters from position 3 (index 2)
      if (!pan && gstin && gstin.length >= 12) {
        pan = gstin.substring(2, 12);
      }
      
      const narrMatch = block.match(/<DBCNARR>([^<]*)<\/DBCNARR>/i);
      const narration = narrMatch ? narrMatch[1].trim() : null;
      
      const grossAmtMatch = block.match(/<DBCGROSSAMT>([^<]*)<\/DBCGROSSAMT>/i);
      const grossAmount = grossAmtMatch ? parseFloat(grossAmtMatch[1].replace(/,/g, '')) || 0 : 0;
      
      // Find all DBCLEDAMT values (including empty ones)
      const ledAmtMatches = block.match(/<DBCLEDAMT>([^<]*)<\/DBCLEDAMT>/gi) || [];
      
      let tdsAmountFromXml = 0;
      const negativeAmounts = [];
      
      ledAmtMatches.forEach(match => {
        const valMatch = match.match(/>([^<]*)</);
        if (valMatch && valMatch[1].trim()) {
          const amt = parseFloat(valMatch[1].replace(/,/g, '')) || 0;
          if (amt > 0 && tdsAmountFromXml === 0) {
            tdsAmountFromXml = amt;
          } else if (amt < 0) {
            negativeAmounts.push(amt);
          }
        }
      });
      
      if (!date) return;
      
      // Skip bank entries
      if (partyName && (partyName.toUpperCase().includes('BANK') || partyName.toUpperCase().includes('CURRENT ACCOUNT'))) {
        return;
      }
      
      // SKIP entries with no voucher number AND no voucher ref - track them for display
      if (!voucherNo) {
        // Only track if there's meaningful data (has TDS amount)
        if (tdsAmountFromXml > 0) {
          skippedEntries.push({
            date: formatTallyDate(date),
            party_name: partyName || 'Unknown',
            voucher_type: voucherType || '',
            tds_amount: tdsAmountFromXml,
            gross_amount: grossAmount,
            reason: 'Missing Voucher No. and Voucher Ref'
          });
        }
        return;
      }
      
      // (debug placeholder kept intentionally for known problem vouchers)
      
      // TDS Amount Logic:
      // 1. If Reference data exists, use it (most accurate from TDS ledger export)
      // 2. Otherwise, use XML TDS amount
      let tdsAmount = tdsAmountFromXml;
      let matchedFromRef = false;
      
      // Try to match with reference data (Reference takes priority when available)
      if (tdsRefMap && voucherNo) {
        const vchType = (voucherType || '').trim();
        const normalizedType = normalizeVoucherType(vchType, masterMappings);
        const compositeKey = normalizedType ? `${normalizedType}-${voucherNo}` : voucherNo;
        
        let refEntry = null;
        
        // Primary match: by normalized composite key
        if (tdsRefMap[compositeKey]) {
          refEntry = tdsRefMap[compositeKey];
        }
        
        // Fallback: try just voucher number without type
        if (!refEntry && tdsRefMap[voucherNo]) {
          refEntry = tdsRefMap[voucherNo];
        }
        
        if (refEntry) {
          // Reference data found - use it (it's from TDS ledger, most accurate)
          tdsAmount = refEntry.tds_amount;
          matchedFromRef = true;
        }
      }
      
      // Keep both positive (TDS deduction) and negative (TDS reversal) entries.
      // Skip only if we have NO TDS amount from either source.
      if (tdsAmount === 0) return;
      
      const isReversal = tdsAmount < 0;
      
      const section = sectionFromFilename || extractSection(narration) || '';
      // Get expense amount directly from Tally XML (largest negative ledger amount)
      const expenseAmount = getExpenseAmountFromXML(grossAmount, negativeAmounts);
      
      // For TCS (206C series), use gross amount as sale turnover
      const isTCS = section && (section.startsWith('206C') || section.includes('TCS'));
      const transactionAmount = isTCS ? grossAmount : expenseAmount;
      
      // Flag data quality issues
      const hasAmountMismatch = transactionAmount === 0 && Math.abs(tdsAmount) > 0;
      
      transactions.push({
        date: formatTallyDate(date),
        voucher_no: voucherNo || '',
        voucher_type: voucherType || '',
        party_name: partyName || '',
        pan: pan || '',
        gstin: gstin || '',
        narration: narration || '',
        amount: transactionAmount,
        gross_amount: grossAmount, // Store original gross for reference
        tds_amount: tdsAmount,
        tds_amount_xml: tdsAmountFromXml,  // Original TDS from XML
        tds_amount_matched: matchedFromRef ? tdsAmount : null,  // TDS from reference match
        section: section,
        // dr_cr: TCS sales are credit collections; everything else (including reversals) is Dr.
        dr_cr: !isReversal && isTCS ? 'Cr' : 'Dr',
        is_tcs: isTCS,
        is_reversal: isReversal,
        // Auto-exclude reversals so they need explicit approval via the Restore button
        is_excluded: isReversal,
        exclusion_reason: isReversal ? 'Pending reversal approval' : null,
        _matchedFromRef: matchedFromRef,
        _amountMismatch: hasAmountMismatch // Flag for UI highlighting
      });
    });
    
    return { transactions, skippedEntries };
  };

  // Parse Tally XML format
  const parseXML = (xmlText, filename = '', tdsRefMap = null, masterMappings = null) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");
    const transactions = [];
    
    const parseError = xmlDoc.querySelector("parsererror");
    if (parseError) {
      throw new Error("Invalid XML format");
    }

    // Check for Columnar format
    const hasColumnarFormat = xmlText.includes('<DBCFIXED>') || 
                              xmlText.includes('<DBCDATE>') || 
                              xmlText.includes('<DBCLEDAMT>') ||
                              xmlText.includes('<DBCCLEDAMT>');
    
    if (hasColumnarFormat) {
      // parseTallyColumnarXML now returns { transactions, skippedEntries }
      return parseTallyColumnarXML(xmlText, filename, tdsRefMap, masterMappings);
    }

    // Standard voucher-based XML (no skipped entries tracking for this format)
    const skippedEntries = [];
    const vouchers = xmlDoc.querySelectorAll("VOUCHER");
    
    if (vouchers.length > 0) {
      vouchers.forEach(voucher => {
        const date = voucher.querySelector("DATE")?.textContent || 
                     voucher.getAttribute("DATE") || "";
        const voucherNo = voucher.querySelector("VOUCHERNUMBER")?.textContent || 
                          voucher.querySelector("REFERENCE")?.textContent || "";
        const voucherType = voucher.querySelector("VOUCHERTYPENAME")?.textContent || "";
        const partyName = voucher.querySelector("PARTYLEDGERNAME")?.textContent || "";
        const narration = voucher.querySelector("NARRATION")?.textContent || "";
        
        const ledgerEntries = voucher.querySelectorAll("ALLLEDGERENTRIES, LEDGERENTRIES");
        ledgerEntries.forEach(entry => {
          const ledgerName = entry.querySelector("LEDGERNAME")?.textContent || "";
          const amount = parseFloat(entry.querySelector("AMOUNT")?.textContent || "0");
          
          if (ledgerName.toUpperCase().includes("TDS") || 
              ledgerName.includes("194") || 
              ledgerName.includes("195") ||
              ledgerName.includes("92B")) {
            
            let finalTdsAmount = Math.abs(amount);
            
            // Override with reference data if available - try composite key first, then simple
            if (tdsRefMap && voucherNo) {
              const vchType = (voucherType || '').trim();
              const compositeKey = vchType ? `${vchType}-${voucherNo}` : null;
              
              const refEntry = (compositeKey && tdsRefMap[compositeKey]) || tdsRefMap[voucherNo];
              if (refEntry) {
                finalTdsAmount = refEntry.tds_amount;
              }
            }
            
            transactions.push({
              date: formatTallyDate(date),
              voucher_no: voucherNo,
              voucher_type: voucherType,
              party_name: partyName,
              tds_ledger: ledgerName,
              tds_amount: finalTdsAmount,
              narration: narration,
              section: extractSection(ledgerName)
            });
          }
        });
      });
    }

    return { transactions, skippedEntries };
  };

  // Format Tally date
  const formatTallyDate = (dateStr) => {
    if (!dateStr) return null;
    const str = dateStr.trim();
    
    if (/^\d{8}$/.test(str)) {
      return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
    }
    
    if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{4}$/.test(str)) {
      const parts = str.split(/[-\/]/);
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
    
    if (/^\d{1,2}-[A-Za-z]{3}-\d{2,4}$/.test(str)) {
      const months = {jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12'};
      const parts = str.split('-');
      const month = months[parts[1].toLowerCase()];
      let year = parts[2];
      if (year.length === 2) {
        year = parseInt(year) > 50 ? `19${year}` : `20${year}`;
      }
      if (month) {
        return `${year}-${month}-${parts[0].padStart(2, '0')}`;
      }
    }
    
    return str;
  };

  // Extract TDS section from ledger name
  const extractSection = (ledgerName) => {
    if (!ledgerName) return null;
    // Match TDS sections: 194A-194T, 194IA, 194IB, 194BA, 194R, etc.
    // Match TCS sections: 206C, 206CA, 206CCA, 206CR, TCS, etc.
    // Match other sections: 195, 192A, 192B, etc.
    
    // First try to match specific section codes
    let match = ledgerName.match(/206C[A-Z()0-9]{0,4}|194[A-Z]{1,2}|195|192[A-Z]?/i);
    if (match) return match[0].toUpperCase();
    
    // Check for generic TCS indicator (maps to 206C)
    if (/\bTCS\b/i.test(ledgerName)) {
      // Try to extract rate from filename like TCS1, TCS0.1
      const rateMatch = ledgerName.match(/TCS\s*(\d+\.?\d*)/i);
      if (rateMatch) {
        const rate = parseFloat(rateMatch[1]);
        if (rate === 1) return '206C(1H)';  // 1% TCS
        if (rate === 0.1 || rate === 0.075) return '206C(1H)';  // 0.1% TCS
      }
      return '206C';  // Default TCS section
    }
    
    return null;
  };

  // Parse CSV format
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const transactions = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const row = {};
      
      headers.forEach((header, idx) => {
        const val = values[idx]?.trim().replace(/^"|"$/g, '') || '';
        const h = header.replace(/[^a-z0-9]/g, '');
        
        if (h.includes('date')) row.date = formatTallyDate(val);
        else if (h.includes('voucherno') || h.includes('vchno') || h.includes('reference')) row.voucher_no = val;
        else if (h.includes('vouchertype') || h.includes('vchtype')) row.voucher_type = val;
        else if (h.includes('party') || h.includes('particulars') || h.includes('ledgername')) row.party_name = val;
        else if (h.includes('tdsledger') || h.includes('tdsaccount')) row.tds_ledger = val;
        else if (h.includes('tdsamount') || h.includes('tdsamt')) {
          row.tds_amount = Math.abs(parseFloat(val.replace(/,/g, '')) || 0);
        }
        else if (h.includes('debit') || h === 'dr') {
          const amt = Math.abs(parseFloat(val.replace(/,/g, '')) || 0);
          if (amt > 0) { row.tds_amount = amt; row.dr_cr = 'Dr'; }
        }
        else if (h.includes('credit') || h === 'cr') {
          const amt = Math.abs(parseFloat(val.replace(/,/g, '')) || 0);
          if (amt > 0) { row.tds_amount = amt; row.dr_cr = 'Cr'; }
        }
        else if (h.includes('amount') && !row.tds_amount) {
          row.amount = Math.abs(parseFloat(val.replace(/,/g, '')) || 0);
        }
        else if (h.includes('section')) row.section = val;
        else if (h.includes('pan')) row.pan = val;
        else if (h.includes('narration')) row.narration = val;
      });
      
      const isTDS = (row.tds_ledger || row.party_name || '').toUpperCase().match(/TDS|194|195|92B/);
      
      if (row.tds_amount > 0 || isTDS) {
        if (!row.tds_ledger && isTDS) {
          row.tds_ledger = row.party_name;
        }
        if (!row.section && row.tds_ledger) {
          row.section = extractSection(row.tds_ledger);
        }
        transactions.push(row);
      }
    }
    
    return transactions;
  };

  // Parse CSV line handling quotes
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    return values;
  };

  // Parse Tally ASCII format
  const parseTallyASCII = (text, filename, tdsRefMap = null, masterMappings = null) => {
    const transactions = [];
    const sectionFromFilename = extractSection(filename) || '';
    
    const lines = text.split('\n').filter(l => l.trim());
    
    for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
      const line = lines[lineIdx];
      const values = parseCSVLine(line);
      
      if (values.length < 10) continue;
      
      const date = values[0]?.trim();
      const partyName = values[1]?.trim().replace(/^"|"$/g, '');
      const voucherType = values[2]?.trim().replace(/^"|"$/g, '');
      const voucherNo = values[3]?.trim().replace(/^"|"$/g, '');
      const gstin = values[4]?.trim().replace(/^"|"$/g, '');
      let pan = values[5]?.trim().replace(/^"|"$/g, '');
      const narration = values[6]?.trim().replace(/^"|"$/g, '');
      const grossAmount = parseFloat(values[8]?.replace(/,/g, '')) || 0;
      
      // Derive PAN from GSTIN if PAN is not available
      if (!pan && gstin && gstin.length >= 12) {
        pan = gstin.substring(2, 12).toUpperCase();
      }
      
      if (!date || grossAmount <= 0) continue;
      if (voucherType?.toLowerCase() === 'payment') continue;
      if (partyName?.toUpperCase().includes('BANK')) continue;
      
      const ledgerAmounts = [];
      for (let i = 9; i < values.length; i++) {
        const amt = parseFloat(values[i]?.replace(/,/g, '')) || 0;
        if (amt !== 0) {
          ledgerAmounts.push(amt);
        }
      }
      
      let tdsAmount = 0;
      const negativeAmounts = [];
      
      for (const amt of ledgerAmounts) {
        if (amt > 0 && tdsAmount === 0) {
          tdsAmount = amt;
        } else if (amt < 0) {
          negativeAmounts.push(amt);
        }
      }
      
      if (tdsAmount <= 0) continue;
      
      // Override with reference data if available - try composite key first, then simple
      if (tdsRefMap && voucherNo) {
        const vchType = (voucherType || '').trim();
        const compositeKey = vchType ? `${vchType}-${voucherNo}` : null;
        
        const refEntry = (compositeKey && tdsRefMap[compositeKey]) || tdsRefMap[voucherNo];
        if (refEntry) {
          tdsAmount = refEntry.tds_amount;
        }
      }
      
      // Get expense amount directly from Tally XML (largest negative ledger amount)
      const expenseAmount = getExpenseAmountFromXML(grossAmount, negativeAmounts);
      
      transactions.push({
        date: formatTallyDate(date),
        voucher_no: voucherNo || '',
        voucher_type: voucherType || '',
        party_name: partyName || '',
        pan: pan || '',
        gstin: gstin || '',
        narration: narration || '',
        amount: expenseAmount,
        tds_amount: tdsAmount,
        section: sectionFromFilename || extractSection(narration) || '',
        dr_cr: 'Dr'
      });
    }
    
    return transactions;
  };

  // Parse Party Details from XML
  const parsePartyXML = (xmlText) => {
    const parties = [];
    
    // Clean XML - handle UTF-16 encoding (spaces between chars) and BOM
    let cleanXml = xmlText
      .replace(/^\uFEFF/, '')  // UTF-8 BOM
      .replace(/^\xEF\xBB\xBF/, '')  // UTF-8 BOM alternate
      .replace(/\0/g, '')  // Remove null bytes (UTF-16 artifact)
      .replace(/ {2,}/g, ' ')  // Collapse multiple spaces
      .trim();
    
    // If XML looks like it has spaced-out characters (UTF-16 artifact), clean it
    if (cleanXml.includes('< D B C') || cleanXml.includes('<D B C') || cleanXml.includes('< M U L T I')) {
      cleanXml = cleanXml.replace(/ /g, '');
    }
    
    
    const seenParties = new Set();
    
    // Format 1: MULTILEDLEDNAME format (Tally Party Master Export)
    if (cleanXml.includes('<MULTILEDLEDNAME>')) {
      
      // Split by MULTILEDSLNO or find all MULTILEDLEDNAME entries
      const ledNameMatches = cleanXml.match(/<MULTILEDLEDNAME>([^<]*)<\/MULTILEDLEDNAME>/gi) || [];
      const ledGstinMatches = cleanXml.match(/<LEDGSTIN>([^<]*)<\/LEDGSTIN>/gi) || [];
      const ledPanMatches = cleanXml.match(/<LEDPAN>([^<]*)<\/LEDPAN>/gi) || 
                           cleanXml.match(/<LEDINCOMETAXNO>([^<]*)<\/LEDINCOMETAXNO>/gi) || [];
      
      
      // Try to parse as blocks between MULTILEDSLNO
      const blocks = cleanXml.split(/<MULTILEDSLNO>/i);
      
      for (let i = 1; i < blocks.length; i++) {
        const block = blocks[i];
        
        const nameMatch = block.match(/<MULTILEDLEDNAME>([^<]*)<\/MULTILEDLEDNAME>/i);
        const partyName = nameMatch ? nameMatch[1].trim() : null;
        
        if (!partyName) continue;
        if (seenParties.has(partyName.toUpperCase())) continue;
        seenParties.add(partyName.toUpperCase());
        
        // Skip bank entries
        if (partyName.toUpperCase().includes('BANK') || partyName.toUpperCase().includes('CURRENT ACCOUNT')) {
          continue;
        }
        
        const gstinMatch = block.match(/<LEDGSTIN>([^<]*)<\/LEDGSTIN>/i) ||
                          block.match(/<MULTILEDGSTIN>([^<]*)<\/MULTILEDGSTIN>/i) ||
                          block.match(/<PARTYGSTIN>([^<]*)<\/PARTYGSTIN>/i);
        const gstin = gstinMatch ? gstinMatch[1].trim() : '';
        
        const panMatch = block.match(/<LEDPAN>([^<]*)<\/LEDPAN>/i) ||
                        block.match(/<LEDINCOMETAXNO>([^<]*)<\/LEDINCOMETAXNO>/i) ||
                        block.match(/<INCOMETAXNUMBER>([^<]*)<\/INCOMETAXNUMBER>/i) ||
                        block.match(/<MULTILEDPAN>([^<]*)<\/MULTILEDPAN>/i);
        let pan = panMatch ? panMatch[1].trim() : '';
        
        // Derive PAN from GSTIN if not available
        if (!pan && gstin && gstin.length >= 12) {
          pan = gstin.substring(2, 12).toUpperCase();
        }
        
        parties.push({
          party_name: partyName,
          pan: pan || '',
          gstin: gstin || '',
          pan_derived: (!panMatch || !panMatch[1].trim()) && !!pan
        });
        
        if (i <= 3) { /* debug placeholder */ }
      }
    }
    
    // Format 2: DBCFIXED format (TDS Transaction Export)
    if (cleanXml.includes('<DBCFIXED>') || cleanXml.includes('<DBCPARTY>')) {
      const blocks = cleanXml.split(/<DBCFIXED>/i);
      
      
      for (let i = 1; i < blocks.length; i++) {
        const block = blocks[i];
        
        const partyMatch = block.match(/<DBCPARTY>([^<]*)<\/DBCPARTY>/i);
        const partyName = partyMatch ? partyMatch[1].trim() : null;
        
        if (!partyName) continue;
        if (seenParties.has(partyName.toUpperCase())) continue;
        seenParties.add(partyName.toUpperCase());
        
        // Skip bank entries
        if (partyName.toUpperCase().includes('BANK') || partyName.toUpperCase().includes('CURRENT ACCOUNT')) {
          continue;
        }
        
        const gstinMatch = block.match(/<DBCGSTIN>([^<]*)<\/DBCGSTIN>/i);
        const gstin = gstinMatch ? gstinMatch[1].trim() : '';
        
        const panMatch = block.match(/<DBCPANNO>([^<]*)<\/DBCPANNO>/i);
        let pan = panMatch ? panMatch[1].trim() : '';
        
        // Derive PAN from GSTIN if not available
        if (!pan && gstin && gstin.length >= 12) {
          pan = gstin.substring(2, 12).toUpperCase();
        }
        
        parties.push({
          party_name: partyName,
          pan: pan || '',
          gstin: gstin || '',
          pan_derived: (!panMatch || !panMatch[1].trim()) && !!pan
        });
        
        if (i <= 3) { /* debug placeholder */ }
      }
    }
    
    // Format 3: LEDGER format (Tally Ledger Export)
    const ledgerMatches = cleanXml.match(/<LEDGER[^>]*>[\s\S]*?<\/LEDGER>/gi) || [];
    
    ledgerMatches.forEach(ledger => {
      const nameMatch = ledger.match(/NAME="([^"]*)"/i) || ledger.match(/<NAME>([^<]*)<\/NAME>/i);
      const partyName = nameMatch ? nameMatch[1].trim() : null;
      
      if (!partyName || seenParties.has(partyName.toUpperCase())) return;
      seenParties.add(partyName.toUpperCase());
      
      const gstinMatch = ledger.match(/<PARTYGSTIN>([^<]*)<\/PARTYGSTIN>/i) || 
                        ledger.match(/<GSTIN>([^<]*)<\/GSTIN>/i);
      const gstin = gstinMatch ? gstinMatch[1].trim() : '';
      
      const panMatch = ledger.match(/<INCOMETAXNUMBER>([^<]*)<\/INCOMETAXNUMBER>/i) ||
                      ledger.match(/<PAN>([^<]*)<\/PAN>/i);
      let pan = panMatch ? panMatch[1].trim() : '';
      
      // Derive PAN from GSTIN if not available
      if (!pan && gstin && gstin.length >= 12) {
        pan = gstin.substring(2, 12).toUpperCase();
      }
      
      parties.push({
        party_name: partyName,
        pan: pan || '',
        gstin: gstin || '',
        pan_derived: !panMatch && !!pan
      });
    });
    
    return parties;
  };

  // Handle Party Details file upload
  const handlePartyFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setUploading(true);
      const text = await readFileWithEncoding(file);
      
      let parties = [];
      
      if (file.name.toLowerCase().endsWith('.xml')) {
        parties = parsePartyXML(text);
      } else if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        // Parse Excel
        const XLSX = await import('xlsx');
        const reader = new FileReader();
        const data = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsArrayBuffer(file);
        });
        
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet);
        
        const seenParties = new Set();
        rows.forEach(row => {
          const partyName = row['Party Name'] || row['party_name'] || row['Name'] || row['PARTY'] || '';
          if (!partyName || seenParties.has(partyName.toUpperCase())) return;
          seenParties.add(partyName.toUpperCase());
          
          const gstin = row['GSTIN'] || row['gstin'] || row['GST'] || '';
          let pan = row['PAN'] || row['pan'] || row['Pan No'] || '';
          
          // Derive PAN from GSTIN if not available
          if (!pan && gstin && gstin.length >= 12) {
            pan = gstin.substring(2, 12).toUpperCase();
          }
          
          if (partyName && (pan || gstin)) {
            parties.push({
              party_name: partyName.trim(),
              pan: pan.toUpperCase().trim(),
              gstin: gstin.toUpperCase().trim(),
              pan_derived: !row['PAN'] && !row['pan'] && !!pan
            });
          }
        });
      }
      
      if (parties.length === 0) {
        toast.error('No party details found in file');
        return;
      }
      
      setPartyData({
        filename: file.name,
        parties,
        total: parties.length,
        withPan: parties.filter(p => p.pan).length,
        withGstin: parties.filter(p => p.gstin).length,
        derivedPan: parties.filter(p => p.pan_derived).length
      });
      
      toast.success(`Found ${parties.length} parties in file`);
    } catch (err) {
      toast.error('Failed to parse party file');
    } finally {
      setUploading(false);
    }
  };

  // Upload party details to backend
  const uploadPartyDetails = async () => {
    if (!partyData?.parties?.length) {
      toast.error('No party data to upload');
      return;
    }
    
    if (!clientId) {
      toast.error('Please select a client first');
      return;
    }
    
    try {
      setUploading(true);
      
      const response = await axios.post(`${API}/api/party-ledgers/bulk`, {
        client_id: clientId,
        parties: partyData.parties.map(p => ({
          party_name: p.party_name,
          pan: p.pan,
          gstin: p.gstin
        }))
      });
      
      setPartyUploadResult(response.data);
      toast.success(`Updated ${response.data.updated || 0} parties, Created ${response.data.created || 0} new parties`);
      
      // Clear the data after successful upload
      setPartyData(null);
      if (partyFileRef.current) {
        partyFileRef.current.value = '';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload party details');
    } finally {
      setUploading(false);
    }
  };

  // Read file with encoding detection
  const readFileWithEncoding = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target.result;
        const uint8 = new Uint8Array(buffer);
        
        const isUtf16 = (uint8[0] === 0xFF && uint8[1] === 0xFE) || 
                        (uint8[1] === 0x00 && uint8[3] === 0x00);
        
        let text;
        if (isUtf16) {
          const decoder = new TextDecoder('utf-16le');
          text = decoder.decode(buffer);
        } else {
          const decoder = new TextDecoder('utf-8');
          text = decoder.decode(buffer);
        }
        
        resolve(text);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle Voucher Type Master file upload
  const handleVchMasterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const isXML = file.name.toLowerCase().endsWith('.xml');
      let result;
      
      if (isXML) {
        result = await parseVoucherTypeMasterXML(file);
      } else {
        result = await parseVoucherTypeMaster(file);
      }
      
      if (result.voucherTypes.length === 0) {
        toast.warning('No voucher type mappings found in file. Make sure the file contains voucher type definitions.');
      } else {
        setVchTypeMaster(result);
        toast.success(`Loaded ${result.voucherTypes.length} voucher type mappings from ${file.name}`);
      }
    } catch (err) {
      toast.error('Failed to parse voucher type master file: ' + err.message);
    }
    
    // Reset input
    if (vchMasterFileRef.current) {
      vchMasterFileRef.current.value = '';
    }
  };

  // Handle TDS Reference file upload (supports multiple files)
  const handleTdsRefUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Get master mappings if available
    const masterMappings = vchTypeMaster?.mappings || null;
    
    try {
      let allEntries = [];
      const allRefMap = {};
      const fileNames = [];
      const sections = new Set();
      const conflicts = []; // Track conflicting voucher numbers
      
      for (const file of files) {
        const text = await readFileWithEncoding(file);
        
        const entries = parseTDSLedgerXML(text, file.name);
        
        if (entries.length > 0) {
          allEntries = [...allEntries, ...entries];
          fileNames.push(file.name);
          
          const section = extractSection(file.name);
          if (section) sections.add(section);
          
          // Add to map ONLY by NORMALIZED composite key: NormalizedVoucherType-VoucherNo
          // This ensures "Jrnl-123" and "Journal-123" both become "journal-123"
          entries.forEach(entry => {
            if (entry.voucher_no) {
              // Normalize voucher type using master mappings if available
              const vchType = (entry.voucher_type || '').trim();
              const normalizedType = normalizeVoucherType(vchType, masterMappings);
              const vchKey = normalizedType ? `${normalizedType}-${entry.voucher_no}` : entry.voucher_no;
              
              // Check if this composite key already exists with different amount
              if (allRefMap[vchKey]) {
                const existing = allRefMap[vchKey];
                if (existing.tds_amount !== entry.tds_amount) {
                  conflicts.push({
                    voucher_no: entry.voucher_no,
                    voucher_type: vchType || '',
                    voucher_key: vchKey,
                    existingAmount: existing.tds_amount,
                    existingFile: existing._sourceFile,
                    newAmount: entry.tds_amount,
                    newFile: file.name
                  });
                }
              }
              
              // Store by composite key
              entry._sourceFile = file.name;
              allRefMap[vchKey] = entry;
              
              // Also create a secondary lookup by TDS amount for fallback matching
              // Key format: "tds-{amount}" (e.g., "tds-30000")
              const tdsKey = `tds-${entry.tds_amount}`;
              if (!allRefMap[tdsKey]) {
                allRefMap[tdsKey] = [];
              }
              allRefMap[tdsKey].push(entry);
              
              // Debug log for first few entries
              if (entries.indexOf(entry) < 3) { /* debug placeholder */ }
            }
          });
        }
      }
      
      if (allEntries.length === 0) {
        toast.error("No TDS entries found in reference file(s)");
        return;
      }
      
      // Warn about conflicts
      if (conflicts.length > 0) {
        toast.warning(`Found ${conflicts.length} voucher(s) with conflicting TDS amounts! Check the conflicts list below.`, { duration: 8000 });
      }
      
      // Debug: Check map content
      
      setTdsRefData({
        filename: fileNames.join(', '),
        fileCount: fileNames.length,
        entries: allEntries,
        map: allRefMap,
        sections: Array.from(sections),
        conflicts: conflicts // Store conflicts for display
      });
      
      toast.success(`Loaded ${allEntries.length} TDS reference entries from ${fileNames.length} file(s)`);
    } catch (err) {
      toast.error("Failed to parse TDS reference file: " + err.message);
    }
    
    if (tdsRefFileRef.current) tdsRefFileRef.current.value = '';
  };

  // Handle Main file upload for reference matching (supports multiple files)
  const handleMainFileUploadRef = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Get master mappings if available
    const masterMappings = vchTypeMaster?.mappings || null;
    
    try {
      let allTransactions = [];
      let allSkipped = [];
      const fileNames = [];
      const tdsRefMap = tdsRefData?.map || null;
      
      for (const file of files) {
        const text = await readFileWithEncoding(file);
        const isXML = file.name.toLowerCase().endsWith('.xml');
        const isTXT = file.name.toLowerCase().endsWith('.txt');
        
        let transactions = [];
        let fileSkipped = [];
        
        if (isXML) {
          const result = parseXML(text, file.name, tdsRefMap, masterMappings);
          transactions = result.transactions || [];
          fileSkipped = result.skippedEntries || [];
        } else if (isTXT) {
          transactions = parseTallyASCII(text, file.name, tdsRefMap, masterMappings);
        } else {
          transactions = parseCSV(text);
        }
        
        if (transactions.length > 0) {
          allTransactions = [...allTransactions, ...transactions];
          fileNames.push(file.name);
        }
        
        // Collect skipped entries
        if (fileSkipped.length > 0) {
          allSkipped = [...allSkipped, ...fileSkipped.map(s => ({ ...s, filename: file.name }))];
        }
      }
      
      // Update skipped entries state
      setSkippedEntries(allSkipped);
      
      if (allTransactions.length === 0) {
        toast.error("No transactions found in main file(s)");
        return;
      }
      
      // Calculate match stats using composite keys and TDS amount fallback
      let matched = 0;
      let unmatched = 0;
      
      if (tdsRefMap) {
        allTransactions.forEach(t => {
          // Use normalized composite key with master mappings
          const vchType = (t.voucher_type || '').trim();
          const normalizedType = normalizeVoucherType(vchType, masterMappings);
          const compositeKey = normalizedType ? `${normalizedType}-${t.voucher_no}` : t.voucher_no;
          
          // Primary: composite key match
          if (tdsRefMap[compositeKey]) {
            matched++;
            t._matched = true;
          } 
          // Fallback: TDS amount match
          else if (t.tds_amount > 0) {
            const tdsKey = `tds-${t.tds_amount}`;
            const tdsCandidates = tdsRefMap[tdsKey];
            if (tdsCandidates && Array.isArray(tdsCandidates) && tdsCandidates.length > 0) {
              matched++;
              t._matched = true;
            } else {
              unmatched++;
              t._matched = false;
            }
          } else {
            unmatched++;
            t._matched = false;
          }
        });
      }
      
      // Separate matched and unmatched for preview
      const unmatchedTxns = allTransactions.filter(t => t._matched === false);
      
      setMainFileData({
        filename: fileNames.join(', '),
        fileCount: fileNames.length,
        transactions: allTransactions,
        unmatchedTransactions: unmatchedTxns
      });
      
      setMatchResult({
        total: allTransactions.length,
        matched: matched,
        unmatched: unmatched,
        transactions: allTransactions // Store transactions for editing
      });
      
      // Check for PAN mismatches and missing party info
      if (existingParties.length > 0) {
        checkPanMismatches(allTransactions);
      }
      
      toast.success(`Loaded ${allTransactions.length} transactions from ${fileNames.length} file(s) (${matched} matched)`);
    } catch (err) {
      toast.error("Failed to parse main file: " + err.message);
    }
    
    if (mainFileRef.current) mainFileRef.current.value = '';
  };

  // Upload matched transactions
  const uploadMatchedTransactions = async () => {
    if (!mainFileData?.transactions || !clientId) {
      toast.error("No transactions to upload");
      return;
    }
    
    setUploading(true);
    
    try {
      const params = new URLSearchParams({
        client_id: clientId,
        filename: mainFileData.filename
      });
      if (tdsRefData?.section) {
        params.append('tds_ledger_name', tdsRefData.section);
      }
      
      const response = await axios.post(
        `${API}/api/transactions/bulk?${params.toString()}`, 
        mainFileData.transactions
      );
      
      setUploadResult({
        success: true,
        total: mainFileData.transactions.length,
        inserted: response.data.inserted || 0,
        errors: response.data.errors || 0,
        mismatches: response.data.mismatches || 0,
        total_amount: response.data.total_amount || 0,
        total_tds: response.data.total_tds || 0
      });
      
      toast.success(`Uploaded ${response.data.inserted} TDS transactions`);
      fetchImportHistory();
      
      // Clear state
      setTdsRefData(null);
      setMainFileData(null);
      setMatchResult(null);
      setSkippedEntries([]);
      
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  // Process a single file (normal mode)
  const processFile = async (file, _uploadMode = 'skip') => {
    const filename = file.name;
    const isXML = filename.toLowerCase().endsWith('.xml');
    const isTXT = filename.toLowerCase().endsWith('.txt');
    const isCSV = filename.toLowerCase().endsWith('.csv');
    
    const text = (isXML || isTXT) ? await readFileWithEncoding(file) : await file.text();
    let transactions = [];
    let skippedEntries = [];
    
    // Determine parser based on import type and file type.
    // parseXML returns { transactions, skippedEntries }; the others return a plain array.
    if (importType === 'tdl' && isCSV) {
      transactions = parseTDLExport(text, filename);
    } else if (isXML) {
      const result = parseXML(text, filename);
      transactions = result?.transactions || [];
      skippedEntries = result?.skippedEntries || [];
    } else if (isTXT) {
      transactions = parseTallyASCII(text, filename);
    } else {
      transactions = parseCSV(text);
    }
    
    return { filename, transactions, skippedEntries };
  };

  // Handle file upload (normal mode)
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !clientId) {
      toast.error("Please select a client first");
      return;
    }

    // Step 1: Parse all files first
    setUploading(true);
    setUploadResult(null);
    setValidationResult(null);
    
    let allTransactions = [];
    let allSkipped = [];
    const fileNames = [];
    
    for (const file of files) {
      try {
        const { transactions, skippedEntries: fileSkipped } = await processFile(file);
        if (transactions.length > 0) {
          allTransactions = allTransactions.concat(transactions);
          fileNames.push(file.name);
        } else {
          toast.warning(`No TDS transactions found in ${file.name}`);
        }
        if (fileSkipped && fileSkipped.length > 0) {
          allSkipped = allSkipped.concat(fileSkipped.map(s => ({ ...s, filename: file.name })));
        }
      } catch (err) {
        toast.error(`Error parsing ${file.name}: ${err.message}`);
      }
    }
    
    // Surface skipped entries so users know what was filtered (e.g., missing voucher no.)
    setSkippedEntries(allSkipped);
    
    if (allTransactions.length === 0) {
      setUploading(false);
      return;
    }
    
    // Step 2: Run pre-import validation
    setValidating(true);
    try {
      const valResponse = await axios.post(
        `${API}/api/transactions/validate-import?client_id=${clientId}`,
        allTransactions
      );
      setValidationResult(valResponse.data);
      
      if (valResponse.data.error_count > 0) {
        toast.warning(`Validation found ${valResponse.data.error_count} errors and ${valResponse.data.warning_count} warnings`);
      }
    } catch (err) {
      // Validation is optional - proceed with upload even if validation endpoint fails
      devWarn("Pre-import validation skipped:", err);
    }
    setValidating(false);
    
    // Step 3: Proceed with upload
    let totalInserted = 0;
    let totalErrors = 0;
    let totalMismatches = 0;
    let totalAmount = 0;
    let totalTds = 0;
    let filesProcessed = 0;
    const skippedFiles = [];
    
    for (const file of files) {
      const filename = file.name;
      
      try {
        const { transactions } = await processFile(file);
        
        if (transactions.length === 0) continue;

        // Check for existing data from this file
        const checkResponse = await axios.get(`${API}/api/import-history?client_id=${clientId}&filename=${encodeURIComponent(filename)}`);
        const existingImport = checkResponse.data?.find(h => h.filename === filename);
        
        if (existingImport && existingImport.inserted > 0) {
          if (files.length === 1) {
            setDuplicateModal({
              open: true,
              filename: filename,
              transactions: transactions,
              existingCount: existingImport.inserted,
              pendingFiles: [],
              currentFileIndex: 0
            });
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
          } else {
            skippedFiles.push({ filename, existingCount: existingImport.inserted });
            continue;
          }
        }

        const params = new URLSearchParams({ client_id: clientId, filename });
        if (customLedgerName.trim()) {
          params.append('tds_ledger_name', customLedgerName.trim());
        }
        
        const response = await axios.post(
          `${API}/api/transactions/bulk?${params.toString()}`, 
          transactions
        );
        
        totalInserted += response.data.inserted || 0;
        totalErrors += response.data.errors || 0;
        totalMismatches += response.data.mismatches || 0;
        totalAmount += response.data.total_amount || 0;
        totalTds += response.data.total_tds || 0;
        filesProcessed++;
        
      } catch (err) {
        totalErrors++;
      }
    }
    
    // Show result
    setUploadResult({
      success: totalInserted > 0,
      total: files.length,
      filesProcessed: filesProcessed,
      inserted: totalInserted,
      errors: totalErrors,
      mismatches: totalMismatches,
      total_amount: totalAmount,
      total_tds: totalTds,
      skippedFiles: skippedFiles
    });
    
    if (totalInserted > 0) {
      const mismatchMsg = totalMismatches > 0 ? ` (${totalMismatches} rate mismatches)` : '';
      toast.success(`Uploaded ${totalInserted} TDS transactions from ${filesProcessed} file(s)${mismatchMsg}`);
    }
    
    if (skippedFiles.length > 0) {
      toast.info(`${skippedFiles.length} duplicate file(s) skipped`);
    }
    
    fetchImportHistory();
    setCustomLedgerName('');
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Handle duplicate file action
  const handleDuplicateAction = async (action) => {
    const { filename, transactions } = duplicateModal;
    setDuplicateModal({ open: false, filename: '', transactions: [], existingCount: 0, pendingFiles: [], currentFileIndex: 0 });
    
    if (action === 'cancel') {
      return;
    }
    
    setUploading(true);
    
    try {
      if (action === 'replace') {
        // Delete existing transactions for this file first
        await axios.delete(`${API}/api/transactions`, { 
          params: { client_id: clientId, filename: filename }
        });
        toast.info(`Cleared existing data for ${filename}`);
      }
      
      // Upload new transactions
      const params = new URLSearchParams({
        client_id: clientId,
        filename: filename
      });
      if (customLedgerName.trim()) {
        params.append('tds_ledger_name', customLedgerName.trim());
      }
      
      const response = await axios.post(
        `${API}/api/transactions/bulk?${params.toString()}`, 
        transactions
      );
      
      setUploadResult({
        success: true,
        total: transactions.length,
        inserted: response.data.inserted || 0,
        errors: response.data.errors || 0,
        mismatches: response.data.mismatches || 0,
        total_amount: response.data.total_amount || 0,
        total_tds: response.data.total_tds || 0,
        action: action === 'replace' ? 'Replaced' : 'Added to'
      });
      
      const mismatchMsg = response.data.mismatches > 0 ? ` (${response.data.mismatches} rate mismatches)` : '';
      toast.success(`${action === 'replace' ? 'Replaced with' : 'Added'} ${response.data.inserted} TDS transactions${mismatchMsg}`);
      fetchImportHistory();
      setCustomLedgerName('');
      
    } catch (err) {
      toast.error("Failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Date,Voucher No,Voucher Type,Party Name,TDS Ledger,TDS Amount,Gross Amount,Section,PAN,Narration
01-Apr-2025,JV/001,Journal,ABC Contractors,TDS ON CONTRACT CO. 194C,2000,100000,194C,ABCDE1234F,Payment for services
01-Apr-2025,PAY/002,Payment,XYZ Professionals,TDS ON PROFESSION CHARGES 194J,10000,100000,194J,XYZPQ5678R,Professional fees`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tds_transactions_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      success: 'bg-green-100 text-green-800',
      partial: 'bg-amber-100 text-amber-800',
      failed: 'bg-red-100 text-red-800'
    };
    const labels = { success: 'Success', partial: 'Partial', failed: 'Failed' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.success}`}>
        {labels[status] || 'Failed'}
      </span>
    );
  };

  if (!selectedClient) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Client Selected</h3>
            <p className="text-slate-600">Please select a client from the dropdown above to import TDS transactions.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="tds-import-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Import TDS Transactions</h1>
        <p className="text-slate-600 mt-1">Upload TDS data from Tally - supports multiple file upload</p>
      </div>

      {/* Import Type Selection */}
      <Tabs value={importType} onValueChange={setImportType} className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-3xl">
          <TabsTrigger value="normal" className="flex items-center gap-2" data-testid="normal-import-tab">
            <FileText className="w-4 h-4" />
            Normal XML
          </TabsTrigger>
          <TabsTrigger value="reference" className="flex items-center gap-2" data-testid="reference-import-tab">
            <Link2 className="w-4 h-4" />
            With TDS Ref
          </TabsTrigger>
          <TabsTrigger value="party" className="flex items-center gap-2" data-testid="party-import-tab">
            <Users className="w-4 h-4" />
            Party Details
          </TabsTrigger>
          <TabsTrigger value="tdl" className="flex items-center gap-2" data-testid="tdl-import-tab">
            <FileCode className="w-4 h-4" />
            TDL CSV
          </TabsTrigger>
        </TabsList>

        {/* Normal XML Import */}
        <TabsContent value="normal" className="mt-4 space-y-4">
          <NormalXMLInstructions />
        </TabsContent>

        {/* Reference Based Import - NEW */}
        <TabsContent value="reference" className="mt-4 space-y-4">
          <ReferenceXMLImport
            mainFileRef={mainFileRef} tdsRefFileRef={tdsRefFileRef} vchMasterFileRef={vchMasterFileRef}
            handleMainFileUploadRef={handleMainFileUploadRef} handleTdsRefUpload={handleTdsRefUpload}
            handleVchMasterUpload={handleVchMasterUpload}
            mainFileData={mainFileData} tdsRefData={tdsRefData} vchTypeMaster={vchTypeMaster}
            matchResult={matchResult} uploading={uploading} uploadMatchedTransactions={uploadMatchedTransactions}
            conflicts={matchResult?.conflicts} skippedEntries={matchResult?.skippedEntries}
            panMismatches={matchResult?.panMismatches} amountMismatches={matchResult?.amountMismatches}
            missingPartyEntries={matchResult?.missingPartyEntries} setPartySearchModal={setPartySearchModal}
            setVchTypeMaster={setVchTypeMaster}
          />
        </TabsContent>

        {/* TDL Export Instructions */}
        <TabsContent value="tdl" className="mt-4 space-y-4">
          <Card className="border-purple-200 bg-purple-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-purple-900">
                <FileCode className="w-5 h-5" />
                TDL-Based CSV Export
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-amber-100 rounded-lg border border-amber-300">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> TDL development is complex. We recommend using the <strong>&ldquo;With TDS Reference&rdquo;</strong> method instead, which works with your existing Tally exports without any TDL setup.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Party Details Upload */}
        <TabsContent value="party" className="mt-4 space-y-4">
          <PartyDetailsTab
            partyFileRef={partyFileRef} handlePartyFileUpload={handlePartyFileUpload}
            partyData={partyData} setPartyData={setPartyData}
            uploading={uploading} uploadPartyDetails={uploadPartyDetails}
            partyUploadResult={partyUploadResult} clientId={clientId}
          />
        </TabsContent>
      </Tabs>

      {/* Upload Section for Normal Mode */}
      {importType === 'normal' && (
        <UploadSection
          fileInputRef={fileInputRef} handleFileUpload={handleFileUpload}
          uploading={uploading} customLedgerName={customLedgerName}
          setCustomLedgerName={setCustomLedgerName}
          downloadTemplate={downloadTemplate} uploadResult={uploadResult}
          validationResult={validationResult} validating={validating}
        />
      )}

      {/* Import History */}
      <ImportHistory importHistory={importHistory} loadingHistory={loadingHistory} />

      {/* Current Client Info */}
      <div className="text-sm text-slate-500">
        Uploading to: <span className="font-medium text-slate-700">{selectedClient?.name}</span>
      </div>

      {/* Modals */}
      <DuplicateFileModal 
        duplicateModal={duplicateModal} 
        setDuplicateModal={setDuplicateModal} 
        handleDuplicateAction={handleDuplicateAction} 
      />
      <ImportPartySearchModal 
        partySearchModal={partySearchModal} 
        setPartySearchModal={setPartySearchModal} 
        existingParties={existingParties} 
        assignPartyToTransaction={assignPartyToTransaction} 
      />
    </div>
  );
};

export default TDSImport;
